import { useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { RadioGroupField } from '@/shared/ui/RadioGroupField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { TextField } from '@/shared/ui/TextField';
import { MAX_EXPIRE_DAYS, savePromoCondition, type PromoConditionValues } from './promoSectionsApi';

const BONUS_TYPE_OPTIONS: SelectOption[] = [
  { value: '%', label: '%' },
  { value: 'RUB', label: 'RUB' },
  { value: 'USD', label: 'USD' },
];

const BURN_OPTIONS: SelectOption[] = [
  { value: '0', label: 'Не сгораемый' },
  { value: '1', label: 'Сгораемый' },
];

const USER_TYPE_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Пользователь' },
  { value: '2', label: 'СТО' },
];

const UTM_FIELDS = [
  { key: 'utmSource', label: 'utm_source', errorKey: 'utm_source' },
  { key: 'utmMedium', label: 'utm_medium', errorKey: 'utm_medium' },
  { key: 'utmCampaign', label: 'utm_campaign', errorKey: 'utm_campaign' },
  { key: 'utmTerm', label: 'utm_term', errorKey: 'utm_term' },
] as const;

type Props = {
  promoId: number;
  /** Set when editing an existing condition. */
  id?: number;
  initial: PromoConditionValues;
  actions: SelectOption[];
  onClose: () => void;
};

export const PromoConditionModal = ({ promoId, id, initial, actions, onClose }: Props) => {
  const qc = useQueryClient();
  const { values, errors, setField, save, isSaving } = useEntityForm(
    initial,
    (v: PromoConditionValues) => savePromoCondition(promoId, id, v),
    () => {
      void qc.invalidateQueries({ queryKey: ['promo-conditions', promoId] });
      onClose();
    },
  );

  const setDays = (value: string) => {
    if (Number(value) <= MAX_EXPIRE_DAYS) setField('days', value, 'expire_time');
  };

  return (
    <ModalDialog
      title={id ? `Редактирование условия #${id}` : 'Создать условие'}
      onClose={onClose}
      onSave={save}
      isSaving={isSaving}
    >
      <FormField label="Перечень типов действий пользователя *" errors={errors.actions}>
        <MultiSelect
          options={actions}
          value={values.actions}
          width={400}
          onChange={(v) => setField('actions', v)}
        />
      </FormField>
      <RadioGroupField
        label="Валюта *"
        name="condition-type-bonus"
        options={BONUS_TYPE_OPTIONS}
        value={values.typeBonus}
        errors={errors.type_bonus}
        onChange={(v) => setField('typeBonus', v, 'type_bonus')}
      />
      <RadioGroupField
        label="Тип бонуса *"
        name="condition-burn"
        options={BURN_OPTIONS}
        value={values.burn}
        errors={errors.burn}
        onChange={(v) => setField('burn', v)}
      />
      {!values.days && (
        <TextField
          label="Дата истечения"
          type="datetime-local"
          value={values.expireTime}
          errors={errors.expire_time}
          onChange={(v) => setField('expireTime', v, 'expire_time')}
        />
      )}
      <TextField
        label="Дата истечения в днях"
        type="number"
        placeholder="Время в днях"
        value={values.days}
        onChange={setDays}
      />
      <TextField
        label="Сумма бонуса *"
        type="number"
        placeholder="Сумма бонуса"
        value={values.bonus}
        errors={errors.bonus}
        onChange={(v) => setField('bonus', v)}
      />
      <TextField
        label="Кол-во использований одним пользователем *"
        type="number"
        placeholder="Кол-во использований"
        value={values.countUse}
        errors={errors.count_use}
        onChange={(v) => setField('countUse', v, 'count_use')}
      />
      {UTM_FIELDS.map(({ key, label, errorKey }) => (
        <TextField
          key={key}
          label={label}
          placeholder={label}
          value={values[key]}
          errors={errors[`utm.${errorKey}`] ?? errors[errorKey]}
          onChange={(v) => setField(key, v, errorKey)}
        />
      ))}
      <RadioGroupField
        label="Тип пользователя *"
        name="condition-user-type"
        options={USER_TYPE_OPTIONS}
        value={values.userType}
        errors={errors.user_type}
        onChange={(v) => setField('userType', v, 'user_type')}
      />
      <ErrorList errors={{ common: errors.common ?? [], server: errors.server ?? [] }} />
    </ModalDialog>
  );
};
